import { Outlet, Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useQuote } from "@/context/QuoteContext";
import { useLang } from "@/i18n/LanguageContext";
import { whatsappLink, PHONE, EMAIL } from "@/lib/api";
import FloatingActions from "@/components/FloatingActions";
import MobileCTABar from "@/components/MobileCTABar";
import SocialProofToasts from "@/components/SocialProofToasts";
import ExitIntent from "@/components/ExitIntent";
import AnnouncementBar from "@/components/AnnouncementBar";
import { Menu, X, Phone, Mail, Facebook, Instagram, Youtube, MessageCircle, Globe } from "lucide-react";

const NAV = [
  { to: "/", key: "home", tid: "home" },
  { to: "/locations", key: "locations", tid: "locations" },
  { to: "/clinics", key: "clinics", tid: "clinics" },
  { to: "/journey", key: "trip", tid: "your-trip" },
  { to: "/stories", key: "stories", tid: "patient-stories" },
  { to: "/blog", key: "guides", tid: "guides" },
];

export default function PublicLayout() {
  const { openQuote } = useQuote();
  const { t, lang, toggleLang } = useLang();
  const { pathname } = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [topBar, setTopBar] = useState(() => !sessionStorage.getItem("mgd_topbar_dismissed"));
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 6);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const LangToggle = ({ className = "", testid }) => (
    <button onClick={toggleLang} className={`inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-sm font-semibold text-foreground/70 hover:text-primary hover:border-primary transition-colors ${className}`} data-testid={testid} aria-label={t("lang.label")}>
      <Globe className="w-4 h-4" /> {t("lang.switchTo")}
    </button>
  );

  return (
    <div className="min-h-screen flex flex-col">
      {/* Top contact bar */}
      {topBar && (
      <div className="bg-primary text-primary-foreground text-xs sm:text-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-9 flex items-center justify-between">
          <a href={`tel:${PHONE.replace(/[^+\d]/g, "")}`} className="flex items-center gap-1.5 hover:opacity-90" data-testid="topbar-phone">
            <Phone className="w-3.5 h-3.5" /> {t("topbar.us")}: {PHONE}
          </a>
          <div className="flex items-center gap-4">
            <a href={`mailto:${EMAIL}`} className="hidden sm:flex items-center gap-1.5 hover:opacity-90" data-testid="topbar-email">
              <Mail className="w-3.5 h-3.5" /> {EMAIL}
            </a>
            <button onClick={() => { sessionStorage.setItem("mgd_topbar_dismissed", "1"); setTopBar(false); }} aria-label="Dismiss" className="opacity-70 hover:opacity-100 transition-opacity" data-testid="topbar-dismiss">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
      )}

      <header className={`sticky top-0 z-40 transition-[background-color,box-shadow,border-color] duration-300 ${scrolled ? "bg-white border-b border-slate-200/70 shadow-[0_6px_24px_-14px_rgba(30,20,50,0.28)]" : "bg-white/70 backdrop-blur-md border-b border-transparent"}`}>
        <div className={`max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between transition-[height] duration-300 ${scrolled ? "h-14" : "h-16"}`}>
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img src="/mgd-logo.png" alt="My Global Dentist" className={`w-auto transition-[height] duration-300 ${scrolled ? "h-8 sm:h-9" : "h-9 sm:h-10"}`} />
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} data-testid={`nav-${n.tid}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${pathname === n.to ? "text-primary bg-primary/5" : "text-foreground/70 hover:text-primary"}`}>
                {t(`nav.${n.key}`)}
              </Link>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-2">
            <LangToggle testid="lang-toggle-desktop" />
            <Button onClick={() => openQuote({ type: "quote" })} className="rounded-full px-5" data-testid="header-get-quote-btn">{t("common.getFreeQuote")}</Button>
          </div>

          <div className="md:hidden flex items-center gap-2">
            <LangToggle testid="lang-toggle-mobile" className="px-2.5 py-1" />
            <button className="p-2" onClick={() => setMobileOpen((o) => !o)} data-testid="mobile-menu-toggle">
              {mobileOpen ? <X /> : <Menu />}
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div className="md:hidden border-t border-border bg-white px-4 py-3 space-y-1">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setMobileOpen(false)}
                className="block px-3 py-3 rounded-lg text-sm font-medium hover:bg-muted" data-testid={`mobile-nav-${n.tid}`}>
                {t(`nav.${n.key}`)}
              </Link>
            ))}
            <Button onClick={() => { setMobileOpen(false); openQuote({ type: "quote" }); }} className="w-full rounded-full mt-2" data-testid="mobile-get-quote-btn">{t("common.getFreeQuote")}</Button>
          </div>
        )}
      </header>

      <main className="flex-1 pb-20 md:pb-0">
        <AnnouncementBar />
        <Outlet />
      </main>

      <footer className="bg-secondary text-white/80 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 grid gap-10 md:grid-cols-4">
          <div className="md:col-span-2">
            <div className="inline-flex bg-white rounded-xl p-2.5 mb-4">
              <img src="/mgd-logo.png" alt="My Global Dentist" className="h-9 w-auto" />
            </div>
            <p className="max-w-sm text-sm text-white/60">{t("footer.blurb")}</p>
            <div className="flex items-center gap-3 mt-5">
              <a href="https://www.facebook.com/profile.php?id=61569728840072" target="_blank" rel="noreferrer" className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors" data-testid="social-facebook"><Facebook className="w-4 h-4" /></a>
              <a href="https://www.instagram.com/myglobaldentistnetwork/" target="_blank" rel="noreferrer" className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors" data-testid="social-instagram"><Instagram className="w-4 h-4" /></a>
              <a href="https://www.youtube.com/@MyGlobalDentist2024" target="_blank" rel="noreferrer" className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors" data-testid="social-youtube"><Youtube className="w-4 h-4" /></a>
              <a href={whatsappLink()} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors" data-testid="social-whatsapp"><MessageCircle className="w-4 h-4" /></a>
            </div>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-3 text-sm">{t("footer.explore")}</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/locations" className="hover:text-white">{t("footer.destinations")}</Link></li>
              <li><Link to="/clinics" className="hover:text-white">{t("footer.compareClinics")}</Link></li>
              <li><Link to="/journey" className="hover:text-white">{t("footer.tripPlan")}</Link></li>
              <li><Link to="/transformations" className="hover:text-white">{t("footer.smileTransformations")}</Link></li>
              <li><Link to="/stories" className="hover:text-white">{t("footer.patientStories")}</Link></li>
              <li><Link to="/blog" className="hover:text-white">{t("footer.guides")}</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-3 text-sm">{t("footer.contact")}</h4>
            <ul className="space-y-2 text-sm text-white/60">
              <li><a href={`tel:${PHONE.replace(/[^+\d]/g, "")}`} className="hover:text-white">{t("topbar.us")}: {PHONE}</a></li>
              <li><a href={`mailto:${EMAIL}`} className="hover:text-white break-all">{EMAIL}</a></li>
              <li><a href={whatsappLink()} target="_blank" rel="noreferrer" className="hover:text-white">{t("footer.chatWhatsApp")}</a></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10 py-5 text-center text-xs text-white/40">© {new Date().getFullYear()} My Global Dentist. {t("footer.rights")}</div>
      </footer>

      <FloatingActions />
      <MobileCTABar />
      <SocialProofToasts />
      <ExitIntent />
    </div>
  );
}
